import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface ScreenerItem {
  prodId: string;
  prodNm: string;
  market: string;
  price: number;
  change: number;
  volume: number;
  mktcap: number;
  perf1w: number | null;
  perf1m: number | null;
  perf3m: number | null;
  perf6m: number | null;
  perfYtd: number | null;
  perf1y: number | null;
  foreignRate: number | null;
}

interface WatchlistGroup {
  id: number;
  name: string;
}

const perfCellStyle = (params: any) => {
  const v = params.value;
  if (v == null) return {};
  return { color: v > 0 ? '#ef4444' : v < 0 ? '#3b82f6' : '#6b7280', fontWeight: 600 };
};

const perfFormatter = (params: any) => {
  const v = params.value;
  if (v == null) return '';
  return v.toFixed(2);
};

const numFormatter = (params: any) => {
  if (params.value == null) return '';
  return params.value.toLocaleString();
};

const mktcapFormatter = (params: any) => {
  if (params.value == null || params.value === 0) return '';
  return Math.round(params.value).toLocaleString();
};

const StockScreener: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const axiosInstance = getMainAxios();
  const { user } = useAuth();

  const [market, setMarket] = useState('ALL');
  const [grpCode, setGrpCode] = useState('ST');
  const [minMktcap, setMinMktcap] = useState(1000);
  const [limit, setLimit] = useState(200);
  const [dates, setDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [items, setItems] = useState<ScreenerItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Chart state
  const [selectedProd, setSelectedProd] = useState<ScreenerItem | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartLoading, setChartLoading] = useState(false);

  // Context menu state
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; item: ScreenerItem } | null>(null);
  const [watchlistGroups, setWatchlistGroups] = useState<WatchlistGroup[]>([]);
  const ctxRef = useRef<HTMLDivElement>(null);

  // 날짜 목록 로드
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/api/v1/quant/screener/dates');
        const d = res.data || [];
        setDates(d);
        if (d.length > 0) setSelectedDate(d[0]);
      } catch (err) {
        console.error('날짜 목록 조회 실패:', err);
      }
    })();
  }, []);

  // 관심종목 그룹 로드
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await axiosInstance.get('/api/v1/watchlist/groups');
        setWatchlistGroups(res.data || []);
      } catch { /* ignore */ }
    })();
  }, [user]);

  // 컨텍스트 메뉴 외부 클릭 닫기
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) setCtxMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { market, grpCode, minMktcap, limit };
      if (selectedDate) params.d = selectedDate;
      const res = await axiosInstance.get('/api/v1/quant/screener', {
        params, timeout: 30000,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('스크리너 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [market, grpCode, minMktcap, limit, selectedDate]);

  // 종목 선택 시 차트 로드
  const loadChart = async (item: ScreenerItem) => {
    setSelectedProd(item);
    setChartLoading(true);
    try {
      const res = await axiosInstance.get('/api/v1/quant/screener/history', {
        params: { prodId: item.prodId }, timeout: 15000,
      });
      setChartData(res.data || []);
    } catch (err) {
      console.error('차트 데이터 조회 실패:', err);
      setChartData([]);
    } finally {
      setChartLoading(false);
    }
  };

  // 관심종목 추가
  const addToWatchlist = async (groupId: number, item: ScreenerItem) => {
    try {
      await axiosInstance.post(`/api/v1/watchlist/groups/${groupId}/items`, { prodId: item.prodId });
      alert(`${item.prodNm} (${item.prodId})을 관심종목에 추가했습니다.`);
    } catch (err: any) {
      const msg = err.response?.data || err.message;
      alert(`추가 실패: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`);
    }
    setCtxMenu(null);
  };


  // Highcharts 옵션
  const chartOptions: Highcharts.Options = selectedProd && chartData.length > 0 ? {
    chart: { height: 280 },
    title: { text: `${selectedProd.prodNm} (${selectedProd.prodId})`, style: { fontSize: '13px' } },
    xAxis: { type: 'datetime' },
    yAxis: [
      { title: { text: '' }, height: '70%', labels: { style: { fontSize: '10px' } } },
      { title: { text: '' }, top: '75%', height: '25%', offset: 0, labels: { style: { fontSize: '10px' } } },
    ],
    series: [
      {
        type: 'candlestick',
        name: '가격',
        data: chartData.map(d => [new Date(d.date).getTime(), d.o, d.h, d.l, d.c]),
        color: '#3b82f6',
        upColor: '#ef4444',
        yAxis: 0,
      },
      {
        type: 'column',
        name: '거래량',
        data: chartData.map(d => [new Date(d.date).getTime(), d.v]),
        color: '#94a3b8',
        yAxis: 1,
      },
    ],
    legend: { enabled: false },
    credits: { enabled: false },
    tooltip: { shared: true },
  } : { title: { text: '' }, credits: { enabled: false } };

  const columnDefs: ColDef<ScreenerItem>[] = [
    {
      headerName: '종목', field: 'prodNm', width: 140, pinned: 'left',
      cellRenderer: (p: any) => {
        const d = p.data;
        if (!d) return '';
        return (
          <div>
            <div style={{ fontWeight: 600, fontSize: '12px' }}>{d.prodNm}</div>
            <div style={{ color: '#9ca3af', fontSize: '10px' }}>{d.prodId}</div>
          </div>
        );
      },
    },
    { headerName: '가격', field: 'price', width: 100, type: 'rightAligned', valueFormatter: numFormatter },
    { headerName: '변화(%)', field: 'change', width: 80, type: 'rightAligned', valueFormatter: perfFormatter, cellStyle: perfCellStyle },
    { headerName: '1주(%)', field: 'perf1w', width: 75, type: 'rightAligned', valueFormatter: perfFormatter, cellStyle: perfCellStyle },
    { headerName: '1월(%)', field: 'perf1m', width: 75, type: 'rightAligned', valueFormatter: perfFormatter, cellStyle: perfCellStyle },
    { headerName: '3월(%)', field: 'perf3m', width: 75, type: 'rightAligned', valueFormatter: perfFormatter, cellStyle: perfCellStyle },
    { headerName: '6월(%)', field: 'perf6m', width: 75, type: 'rightAligned', valueFormatter: perfFormatter, cellStyle: perfCellStyle },
    { headerName: 'YTD(%)', field: 'perfYtd', width: 75, type: 'rightAligned', valueFormatter: perfFormatter, cellStyle: perfCellStyle },
    { headerName: '1년(%)', field: 'perf1y', width: 80, type: 'rightAligned', valueFormatter: perfFormatter, cellStyle: perfCellStyle },
    { headerName: '시총(억)', field: 'mktcap', width: 95, type: 'rightAligned', valueFormatter: mktcapFormatter },
    { headerName: '거래량', field: 'volume', width: 95, type: 'rightAligned', valueFormatter: numFormatter },
    { headerName: '외국인(%)', field: 'foreignRate', width: 80, type: 'rightAligned', valueFormatter: (p: any) => p.value != null ? p.value.toFixed(1) : '' },
    { headerName: '시장', field: 'market', width: 70 },
  ];

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 필터 바 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* 날짜 */}
          <label style={labelStyle}>기준일</label>
          <select value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={selectStyle}>
            {dates.map(d => <option key={d} value={d}>{d}</option>)}
            {dates.length === 0 && <option value="">로딩 중...</option>}
          </select>

          <span style={{ color: '#d1d5db' }}>|</span>

          {/* 시장 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={labelStyle}>시장</label>
            {['ALL', 'KOSPI', 'KOSDAQ'].map(v => (
              <button key={v} onClick={() => setMarket(v)} style={market === v ? btnActiveStyle : btnStyle}>
                {v === 'ALL' ? '전체' : v}
              </button>
            ))}
          </div>

          <span style={{ color: '#d1d5db' }}>|</span>

          {/* 종목유형 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={labelStyle}>유형</label>
            {[['ALL', '전체'], ['ST', '주식'], ['EF', 'ETF']].map(([v, l]) => (
              <button key={v} onClick={() => setGrpCode(v)} style={grpCode === v ? btnActiveStyle : btnStyle}>
                {l}
              </button>
            ))}
          </div>

          <span style={{ color: '#d1d5db' }}>|</span>

          {/* 시가총액 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={labelStyle}>시총</label>
            {[[0, '전체'], [100, '100억+'], [1000, '1000억+'], [10000, '1조+']].map(([v, l]) => (
              <button key={String(v)} onClick={() => setMinMktcap(v as number)} style={minMktcap === v ? btnActiveStyle : btnStyle}>
                {l as string}
              </button>
            ))}
          </div>

          <span style={{ color: '#d1d5db' }}>|</span>

          {/* 표시 수 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={labelStyle}>표시</label>
            {[100, 200, 500].map(v => (
              <button key={v} onClick={() => setLimit(v)} style={limit === v ? btnActiveStyle : btnStyle}>{v}</button>
            ))}
          </div>

          <span style={{ color: '#d1d5db' }}>|</span>

          <button onClick={fetchData} disabled={loading}
            style={{ padding: '5px 20px', borderRadius: '3px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', backgroundColor: loading ? '#9ca3af' : '#22c55e', color: 'white' }}>
            {loading ? '조회 중...' : '조회'}
          </button>

          <div style={{ flex: 1 }} />
          <span style={{ fontSize: '11px', color: '#6b7280' }}>{items.length} / {total}개</span>
        </div>
      </div>

      {/* 그리드 (상단 50%) */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
        <div
          className="ag-theme-quartz"
          style={{ height: 'calc(50vh - 120px)', width: '100%' }}
          onContextMenu={(e) => {
            e.preventDefault();
            const target = e.target as HTMLElement;
            const rowElement = target.closest('.ag-row');
            if (rowElement) {
              const rowIndex = parseInt(rowElement.getAttribute('row-index') || '-1');
              if (rowIndex >= 0 && rowIndex < items.length) {
                setCtxMenu({ x: e.clientX, y: e.clientY, item: items[rowIndex] });
              }
            }
          }}
        >
          <AgGridReact<ScreenerItem> {...copyMenu.gridProps}
            rowData={items}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true, sortable: true, filter: true }}
            getRowId={p => p.data.prodId}
            animateRows={true}
            rowHeight={36}
            headerHeight={32}
            rowSelection={{ mode: 'singleRow', enableClickSelection: true, checkboxes: false }}
            onRowClicked={e => { if (e.data) loadChart(e.data); }}
            suppressContextMenu={true}
          />
        </div>
      </div>

      {/* 차트 (하단) */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden', padding: '12px' }}>
        {chartLoading ? (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '12px' }}>차트 로딩 중...</div>
        ) : selectedProd && chartData.length > 0 ? (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        ) : (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '12px' }}>
            종목을 클릭하면 차트가 표시됩니다.
          </div>
        )}
      </div>

      {/* 컨텍스트 메뉴 */}
      {ctxMenu && (
        <div ref={ctxRef} style={{
          position: 'fixed', left: ctxMenu.x, top: ctxMenu.y, zIndex: 9999,
          backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '4px 0', minWidth: '180px',
        }}>
          <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, color: '#1e40af', borderBottom: '1px solid #e5e7eb' }}>
            {ctxMenu.item.prodNm} ({ctxMenu.item.prodId})
          </div>
          {watchlistGroups.length > 0 ? (
            watchlistGroups.map(g => (
              <div key={g.id} onClick={() => addToWatchlist(g.id, ctxMenu.item)}
                style={{ padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#374151' }}
                onMouseEnter={e => (e.target as HTMLElement).style.backgroundColor = '#eff6ff'}
                onMouseLeave={e => (e.target as HTMLElement).style.backgroundColor = 'white'}>
                관심종목 추가 → {g.name}
              </div>
            ))
          ) : (
            <div style={{ padding: '6px 12px', fontSize: '11px', color: '#9ca3af' }}>관심종목 그룹이 없습니다.</div>
          )}
          <div style={{ borderTop: '1px solid #e5e7eb' }}>
            <div onClick={() => { loadChart(ctxMenu.item); setCtxMenu(null); }}
              style={{ padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#374151' }}
              onMouseEnter={e => (e.target as HTMLElement).style.backgroundColor = '#eff6ff'}
              onMouseLeave={e => (e.target as HTMLElement).style.backgroundColor = 'white'}>
              차트 보기
            </div>
          </div>
        </div>
      )}
      {copyMenu.menu}
    </div>
  );
};

const labelStyle: React.CSSProperties = { fontSize: '11px', fontWeight: 600, color: '#1e40af', whiteSpace: 'nowrap' };
const btnStyle: React.CSSProperties = { padding: '4px 10px', borderRadius: '3px', border: 'none', backgroundColor: '#e5e7eb', color: '#374151', cursor: 'pointer', fontSize: '11px' };
const btnActiveStyle: React.CSSProperties = { ...btnStyle, backgroundColor: '#3b82f6', color: 'white' };
const selectStyle: React.CSSProperties = { padding: '4px 8px', borderRadius: '3px', border: '1px solid #d1d5db', fontSize: '12px', backgroundColor: 'white' };

export default StockScreener;
